import React, { useState, useEffect, useCallback } from 'react';
import { Building2, TrendingUp, AlertTriangle, Activity, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { useFacility } from '../context/FacilityContext';
import portfolioService from '../services/portfolioService';

// Recharts doesn't read CSS vars — pull --c-brand off the documentElement
// so chart strokes track the brand axis. Re-reads on data-brand changes
// so the chart re-renders with the new hue without remounting.
const useBrandColor = () => {
  const [color, setColor] = useState('#2563eb');
  useEffect(() => {
    const read = () => {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue('--c-brand')
        .trim();
      if (v) setColor(v);
    };
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-brand', 'class'],
    });
    return () => obs.disconnect();
  }, []);
  return color;
};

const Portfolio = () => {
  const { facilityId } = useFacility();
  const [overview, setOverview] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [churn, setChurn] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const brandColor = useBrandColor();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pr, ch, fc] = await Promise.all([
        portfolioService.getOverview(),
        facilityId ? portfolioService.getPricingRecommendations(facilityId) : Promise.resolve({ data: [] }),
        facilityId ? portfolioService.getChurnRisk(facilityId, 25) : Promise.resolve({ data: [] }),
        facilityId ? portfolioService.getDemandForecast(facilityId) : Promise.resolve({ data: null }),
      ]);
      setOverview(ov.data);
      setPricing(pr.data || []);
      setChurn(ch.data || []);
      setForecast(fc.data);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Portfolio & Predictions</h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Cross-facility rollup, pricing nudges, churn risk, and demand forecast.
        </p>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <KpiCard
            icon={Building2}
            label="Facilities"
            value={overview.total_facilities}
            color="text-blue-600"
            bg="bg-blue-50"
          />
          <KpiCard
            icon={Activity}
            label="Occupancy"
            value={`${overview.occupancy_rate}%`}
            sub={`${overview.occupied_units}/${overview.total_units} units`}
            color="text-emerald-600"
            bg="bg-emerald-50"
          />
          <KpiCard
            icon={TrendingUp}
            label="MTD Revenue"
            value={`$${(overview.monthly_revenue || 0).toLocaleString()}`}
            color="text-violet-600"
            bg="bg-violet-50"
          />
          <KpiCard
            icon={AlertTriangle}
            label="Churn Risks"
            value={churn.length}
            color="text-amber-600"
            bg="bg-amber-50"
          />
        </div>
      )}

      {overview?.facilities?.length > 0 && (
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden mb-5">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-[13px] font-semibold text-slate-700">Facility Rollup</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-5 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase">Facility</th>
                  <th className="px-5 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Units</th>
                  <th className="px-5 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Occupancy</th>
                  <th className="px-5 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">MTD Revenue</th>
                </tr>
              </thead>
              <tbody>
                {overview.facilities.map((f) => (
                  <tr key={f.facility_id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2.5 font-medium text-slate-800">{f.name}</td>
                    <td className="px-5 py-2.5 text-right text-slate-600">{f.total_units}</td>
                    <td className="px-5 py-2.5 text-right text-slate-600">{f.occupancy_rate}%</td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-700">
                      ${(f.monthly_revenue || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-[13px] font-semibold text-slate-700">Pricing Recommendations</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {pricing.map((rec) => (
              <div key={rec.unit_type} className="px-5 py-3 flex items-center gap-3">
                <ActionIcon action={rec.action} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-slate-800">{rec.unit_type}</p>
                  <p className="text-[11px] text-slate-500">{rec.reason}</p>
                </div>
                <div className="text-right">
                  <p className="text-[12px] text-slate-400 line-through">${rec.current_avg_rate}</p>
                  <p className="text-[13px] font-semibold text-slate-800">${rec.suggested_rate}</p>
                </div>
              </div>
            ))}
            {pricing.length === 0 && (
              <p className="text-center text-[12px] text-slate-400 py-6">No unit data.</p>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-[13px] font-semibold text-slate-700">Churn Risk</h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {churn.map((c) => (
              <div key={c.rental_id} className="px-5 py-3 flex items-center gap-3">
                <RiskBadge score={c.score} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-slate-800 truncate">{c.customer}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    Unit {c.unit} · {c.reasons.slice(0, 2).join(' · ')}
                  </p>
                </div>
                <p className="text-[12px] font-mono text-slate-600">${c.balance}</p>
              </div>
            ))}
            {churn.length === 0 && (
              <p className="text-center text-[12px] text-slate-400 py-6">No high-risk rentals.</p>
            )}
          </div>
        </div>
      </div>

      {forecast && forecast.history?.length > 0 && (
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-[13px] font-semibold text-slate-700">Demand Forecast</h2>
            <span className="text-[11px] text-slate-500">
              Next month projection: <span className="font-semibold text-slate-800">{forecast.forecast_next_month}</span>
              {' '}
              ({forecast.confidence_low}–{forecast.confidence_high})
            </span>
          </div>
          <div className="px-5 py-4">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={forecast.history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip />
                <ReferenceLine
                  y={forecast.forecast_next_month}
                  stroke="#10b981"
                  strokeDasharray="4 4"
                  label={{ value: 'Forecast', position: 'right', fill: '#10b981', fontSize: 10 }}
                />
                <Line
                  type="monotone"
                  dataKey="move_ins"
                  stroke={brandColor}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color, bg }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center`}>
        <Icon className={`h-3.5 w-3.5 ${color}`} />
      </div>
    </div>
    <p className="text-xl font-bold text-slate-900">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const ActionIcon = ({ action }) => {
  if (action === 'raise') {
    return (
      <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
        <ArrowUp className="h-3.5 w-3.5 text-emerald-600" />
      </div>
    );
  }
  if (action === 'lower') {
    return (
      <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
        <ArrowDown className="h-3.5 w-3.5 text-red-600" />
      </div>
    );
  }
  return (
    <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center">
      <Minus className="h-3.5 w-3.5 text-slate-500" />
    </div>
  );
};

const RiskBadge = ({ score }) => {
  const color = score >= 70 ? 'bg-red-100 text-red-700' :
                score >= 50 ? 'bg-amber-100 text-amber-700' :
                'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg font-bold text-[12px] ${color}`}>
      {score}
    </span>
  );
};

export default Portfolio;
